import React, { useState } from 'react';
import { CalendarDaysIcon, ImageIcon, MegaphoneIcon, MousePointerClickIcon, TrendingUpIcon, UsersRoundIcon } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
type Channel = {
  name: string;
  iconUrl?: string;
  spend: string;
  revenue: string;
  roas: string;
  share: number;
};
const spendByMonth = [{
  label: 'Jan',
  spend: 1850,
  revenue: 8420
}, {
  label: 'Feb',
  spend: 2260,
  revenue: 10180
}, {
  label: 'Mar',
  spend: 1980,
  revenue: 8940
}, {
  label: 'Apr',
  spend: 2540,
  revenue: 12360
}, {
  label: 'May',
  spend: 3120,
  revenue: 15870
}, {
  label: 'Jun',
  spend: 2870,
  revenue: 14210
}];
const eddyChannels: Channel[] = [{
  name: 'Manymoons',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg',
  spend: '$9,480',
  revenue: '$52,140',
  roas: '5.5x',
  share: 45
}, {
  name: 'Vibe ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/4QsPQBQikeUopo55GPTpMC/vibe.svg',
  spend: '$5,265',
  revenue: '$21,060',
  roas: '4.0x',
  share: 25
}, {
  name: 'Meta + Instagram',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/3EsJsRbrqwgztzCzWf85MT/meta.svg',
  spend: '$4,212',
  revenue: '$14,320',
  roas: '3.4x',
  share: 20
}, {
  name: 'Email + SMS',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/3rX7mUrbpBP38ioN5pJ69R/Klaviyo.svg',
  spend: '$2,103',
  revenue: '$9,880',
  roas: '4.7x',
  share: 10
}];
const contentPushes = [{
  month: 'Month 1',
  title: 'Manymoons partnership announcement',
  status: 'Live',
  detail: 'Editorial feature plus paid amplification.'
}, {
  month: 'Month 2',
  title: 'Spring Collection recovery edit',
  status: 'Scheduled',
  detail: 'Jackets and cardigans lead the creative.'
}, {
  month: 'Month 3',
  title: 'Coordinated sets feature',
  status: 'Planned',
  detail: 'Tops and bottoms merchandised as pairs.'
}];
export function MarketingDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [period, setPeriod] = useState<'Year to date' | 'Quarter to date'>('Year to date');
  const hasData = selectedOwner === 'Eddy';
  const channels = hasData ? eddyChannels : [];
  const totals = hasData ? {
    spend: '$21,060',
    revenue: '$97,400',
    roas: '4.6x',
    sellThrough: '38%'
  } : {
    spend: '$0',
    revenue: '$0',
    roas: '—',
    sellThrough: '0%'
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="marketing-heading" data-id="element-1026" data-mp="30ly9n7tvj8sy">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1027" data-mp="2vmlmelz3bzpu">
        <div data-id="element-1028" data-mp="2qutua70adlxl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1029" data-mp="2qu263h90qqhc">
            <MegaphoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1030" data-mp="2lu63nmfyz73w" />
            Marketing
          </span>
          <h1 id="marketing-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1031" data-mp="2cvug5taegpfo">
            Marketing performance
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1032" data-mp="27x9d7m3rjrnj">
            {selectedOwner ? `How Carli is investing marketing dollars to accelerate sell-through for ${selectedOwner}.` : 'Select a brand in the sidebar to see how marketing spend is driving recovery.'}
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1033" data-mp="11e84sdnpg3if">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1034" data-mp="4pih26pecgoj" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Marketing summary" data-id="element-1035" data-mp="9kxtcu7ra4iu">
        <SummaryCard icon={MegaphoneIcon} label="Marketing spend" value={totals.spend} detail="Allocated by Carli" data-id="element-1036" data-mp="iflz0lh8to86" />
        <SummaryCard icon={TrendingUpIcon} label="Attributed revenue" value={totals.revenue} detail="From promoted inventory" data-id="element-1037" data-mp="3i3dlbjs3mfne" />
        <SummaryCard icon={MousePointerClickIcon} label="Return on ad spend" value={totals.roas} detail="Blended across channels" data-id="element-1038" data-mp="3m3nqiu82h217" />
        <SummaryCard icon={UsersRoundIcon} label="Promoted sell-through" value={totals.sellThrough} detail="Units sold from campaigns" data-id="element-1039" data-mp="32xzmwcpp62hk" />
      </section>

      <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-label="Spend versus attributed revenue" data-id="element-1040" data-mp="3cq5cjfsh87tl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1041" data-mp="37xvoy6eu3juh">
          <div data-id="element-1042" data-mp="3loqt55ee1crq">
            <h2 className="text-lg font-semibold" data-id="element-1043" data-mp="3lseng6rnq37i">Spend vs. attributed revenue</h2>
            <p className="mt-1 text-sm text-ink/60" data-id="element-1044" data-mp="3gr8uq8xzyfxz">Marketing investment measured against recovered revenue.</p>
          </div>
          <div className="flex gap-1 rounded-xl border border-ink/10 bg-cream p-1" data-id="element-1045" data-mp="3qpoko8lafj5w">
            {(['Quarter to date', 'Year to date'] as const).map(item => <button key={item} type="button" onClick={() => setPeriod(item)} aria-pressed={period === item} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${period === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-1046" data-mp="21b9ynw1nmae3">
                {item}
              </button>)}
          </div>
        </div>
        <div className="mt-6 h-64 sm:h-72" data-id="element-1047" data-mp="71lbpjh6cydg">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1048" data-mp="70jraoe9qzj2">
            <BarChart data={period === 'Year to date' ? spendByMonth : spendByMonth.slice(-3)} margin={{
            top: 8,
            right: 8,
            left: -15,
            bottom: 0
          }} barCategoryGap="22%" barGap={6} data-id="element-1049" data-mp="3p7ptte7pw6y8">
              <CartesianGrid vertical={false} stroke="#1C1A17" strokeOpacity={0.1} strokeDasharray="3 5" data-id="element-1050" data-mp="3fbd5kvuxylav" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} dy={8} data-id="element-1051" data-mp="3kahfxptlqp2s" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => `$${Math.round(value / 1000)}k`} data-id="element-1052" data-mp="1djrnsewup83m" />
              <Tooltip content={<ChartTooltip data-id="element-1054" data-mp="1h1nprtmm8vjn" />} cursor={{
              fill: '#BB5E40',
              fillOpacity: 0.06
            }} data-id="element-1053" data-mp="1h15w94rc8vff" />
              <Bar dataKey="spend" name="Spend" fill="#BB5E40" radius={[6, 6, 0, 0]} maxBarSize={64} data-id="element-1055" data-mp="1c1jhurzrx951" />
              <Bar dataKey="revenue" name="Attributed revenue" fill="#4F8A5B" radius={[6, 6, 0, 0]} maxBarSize={64} data-id="element-1056" data-mp="173rijcwsukqt" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink/60" data-id="element-1057" data-mp="1zrx23wc4a3p2">
          <span className="inline-flex items-center gap-2" data-id="element-1058" data-mp="1usaqfrbxsvid"><span className="h-2.5 w-2.5 rounded-full bg-[#BB5E40]" data-id="element-1059" data-mp="1utltfoxk5z8f" />Marketing spend</span>
          <span className="inline-flex items-center gap-2" data-id="element-1060" data-mp="1q0iwr7xhi9o0"><span className="h-2.5 w-2.5 rounded-full bg-[#4F8A5B]" data-id="element-1061" data-mp="1pz7trabv55xy" />Attributed revenue</span>
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]" data-id="element-1062" data-mp="2ihljsnqyjnmh">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="marketing-channels-heading" data-id="element-1063" data-mp="2ijy77gfhiq6x">
          <div className="flex items-center gap-2" data-id="element-1064" data-mp="2m0atp6rf46m8">
            <MegaphoneIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1065" data-mp="2m1vqz454a3ns" />
            <h2 id="marketing-channels-heading" className="text-base font-semibold" data-id="element-1066" data-mp="2q1wf5gi0pt81">Spend by channel</h2>
          </div>
          {channels.length > 0 ? <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1067" data-mp="3003jmzv4eit5">
              <table className="w-full min-w-[520px] text-left text-xs" data-id="element-1068" data-mp="2zxhcv2g207bz">
                <thead className="border-b border-ink/10 text-ink/50" data-id="element-1069" data-mp="34y5e0gxzy4jx">
                  <tr data-id="element-1070" data-mp="38zgsprfc15dc">
                    <th scope="col" className="pb-2 font-semibold" data-id="element-1071" data-mp="38yxkx3jebxd1">Channel</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1072" data-mp="3dqp6t0rlxewu">Spend</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1073" data-mp="3iqlbz3c1jcdz">Revenue</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1074" data-mp="3npekehch0d7a">ROAS</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1075" data-mp="3so96bs5qihvm">% of spend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-1076" data-mp="hsqzkkwbx165">
                  {channels.map(channel => <tr key={channel.name} data-id="element-1077" data-mp="ctxiyjlqwu1r">
                      <th scope="row" className="py-3 text-left font-medium text-ink" data-id="element-1078" data-mp="qui0zdju6lu7">
                        <span className="flex items-center gap-2.5" data-id="element-1079" data-mp="qs491dkgdpqv">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] p-1.5" data-id="element-1080" data-mp="lvx15f8hy46a">
                            {channel.iconUrl ? <img src={channel.iconUrl} alt="" className="h-full w-full object-contain" data-id="element-1081" data-mp="3qan1t7ks5jm4" /> : <MegaphoneIcon className="h-4 w-4 text-cream" aria-hidden="true" data-id="element-1082" data-mp="7xp7phao5kzv" />}
                          </span>
                          {channel.name}
                        </span>
                      </th>
                      <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1083" data-mp="36me7orzof3jz">{channel.spend}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1084" data-mp="1m2iurbzoxylh">{channel.revenue}</td>
                      <td className="py-3 text-right tabular-nums text-forest" data-id="element-1085" data-mp="1qv3oyemiedpj">{channel.roas}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1086" data-mp="1d46xws88e4kl">{channel.share}%</td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <p className="py-8 text-center text-sm text-ink/55" data-id="element-1087" data-mp="25scinezabtkf">Select a brand to view marketing spend by channel.</p>}
        </article>

        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="content-calendar-heading" data-id="element-1088" data-mp="1vtwr5awciiae">
          <div className="flex items-center gap-2" data-id="element-1089" data-mp="20rezp06gavhv">
            <CalendarDaysIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1090" data-mp="ke830g7cvd80" />
            <h2 id="content-calendar-heading" className="text-base font-semibold" data-id="element-1091" data-mp="kgb17ninwcnt">Content pushes</h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-ink/55" data-id="element-1092" data-mp="t58jfixvn1lc">Monthly partnership promotion for the first three months.</p>
          <ol className="mt-5 space-y-3" data-id="element-1093" data-mp="y4l90bg4umrm">
            {contentPushes.map(push => <li key={push.title} className="rounded-xl border border-ink/10 bg-cream/45 p-4" data-id="element-1094" data-mp="jfxfeapukn4b">
                <div className="flex items-center justify-between gap-3" data-id="element-1095" data-mp="odyxotfg8ieq">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-1096" data-mp="oc5qnd0xbw67">{push.month}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${push.status === 'Live' ? 'bg-forest/10 text-forest' : push.status === 'Scheduled' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-ink/7 text-ink/55'}`} data-id="element-1097" data-mp="eg2ow89zhzjs">{push.status}</span>
                </div>
                <p className="mt-2 text-sm font-semibold" data-id="element-1098" data-mp="1uu2ofi4x3n6i">{push.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-ink/55" data-id="element-1099" data-mp="1ztnl1lwm7jg2">{push.detail}</p>
              </li>)}
          </ol>
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#BB5E40]/5 p-3" data-id="element-1100" data-mp="3kawupegqsv25">
            <ImageIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1101" data-mp="3fd502copquu9" />
            <p className="text-xs leading-relaxed text-ink/60" data-id="element-1102" data-mp="3u217q8muy5uc">Request product placement in Manymoons’ Vibe ads alongside each monthly push.</p>
          </div>
        </article>
      </section>
    </div>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof MegaphoneIcon;
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1103" data-mp="202xhm644v6o">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1104" data-mp="gu7vyeneipn1" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-1105" data-mp="bwxxk9i1b3n2">{label}</p>
      <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1106" data-mp="bu1w4b6z9wtv">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-1107" data-mp="2io73coxekmug">{detail}</p>
    </article>;
}
function ChartTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{
    value?: number;
    name?: string;
    color?: string;
  }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return <div className="rounded-xl border border-ink/10 bg-paper px-3 py-2 shadow-lg" data-id="element-1108" data-mp="3cc36jh9ru38j">
      <p className="text-xs text-ink/50" data-id="element-1109" data-mp="3ce63ymd96yna">{label}</p>
      {payload.map(entry => <p key={entry.name} className="mt-1 flex items-center gap-2 text-sm font-semibold tabular-nums text-ink" data-id="element-1110" data-mp="3hchivbienzcd">
          <span className="h-2 w-2 rounded-full" style={{
        backgroundColor: entry.color
      }} data-id="element-1111" data-mp="3m8gb5a6uu1i2" />
          {entry.name}: {(entry.value ?? 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      })}
        </p>)}
    </div>;
}